/**
 * BusinessCaseView — a hire request's business case, folded under one line:
 * why the hire is needed and the selection approach the hiring manager gave
 * (HR's template, 20 Oct 2026).
 *
 * Shown on the manager's own requests, on HR Ops' Position requests and on
 * Role Master's Requests tab. A request raised before the template has its
 * why alone, and says so.
 */
import React from 'react';
import type { SelectionApproach } from '../api/services/hireRequestService';
import { describeBusinessCase } from './selectionApproach';

interface Props {
  why?: string | null;
  approach?: SelectionApproach | null;
  /** The request's campus, for "Only at PT". */
  campus?: string | null;
  className?: string;
}

const BusinessCaseView: React.FC<Props> = ({ why, approach, campus, className }) => {
  const sections = describeBusinessCase(why, approach, campus);
  if (!sections.length) return null;
  return (
    <details className={`text-xs ${className || ''}`}>
      <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">Business case</summary>
      <div className="mt-2 space-y-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-800">
        {!approach && (
          <p className="text-gray-500">
            Raised before the business case asked how the role is advertised and filled.
          </p>
        )}
        {sections.map((s) => (
          <section key={s.title}>
            <h4 className="font-semibold text-gray-900">{s.title}</h4>
            <dl className="mt-1 grid gap-x-4 gap-y-1 sm:grid-cols-[12rem_1fr]">
              {s.rows.map((r) => (r.label ? (
                <React.Fragment key={r.label}>
                  <dt className="text-gray-500">{r.label}</dt>
                  <dd className="whitespace-pre-line">{r.value}</dd>
                </React.Fragment>
              ) : (
                <dd key={r.value} className="whitespace-pre-line sm:col-span-2">{r.value}</dd>
              )))}
            </dl>
          </section>
        ))}
      </div>
    </details>
  );
};

export default BusinessCaseView;
